import { Plus, Trash2, X } from 'lucide-react'

import { Button, SelectField, StatusMessage, TextField } from '@krp/ui-kit'

import type { PriceManageActions, PriceManageVm } from '../model/priceManage'
import './onec-ui.css'

/**
 * Форма «Создать цену» окна «Прайс 1С»: товар (поиск по базе), вид цен, цена, дата. Запись идёт в регистр цен 1С; ошибка 1С
 * показывается её текстом. Экран получает vm и actions и больше ничего (R02).
 */
export function PriceCreatePanel({
  vm,
  actions,
  priceTypes,
}: {
  vm: PriceManageVm
  actions: PriceManageActions
  priceTypes: readonly { value: string; label: string }[]
}) {
  if (!vm.open) {
    return (
      <div className="onec-row">
        <Button variant="primary" icon={<Plus size={16} aria-hidden="true" />} onClick={actions.openForm}>
          Создать цену
        </Button>
        {vm.done !== null && (
          <p className="onec-muted" role="status">
            {vm.done}
          </p>
        )}
      </div>
    )
  }
  const { draft } = vm
  return (
    <section className="onec-section" aria-labelledby="onec-price-create-title">
      <div className="onec-section__head">
        <h3 id="onec-price-create-title">Новая цена</h3>
        <Button variant="ghost" icon={<X size={16} aria-hidden="true" />} onClick={actions.closeForm}>
          Закрыть
        </Button>
      </div>
      {draft.product === null ? (
        <>
          <TextField
            label="Товар: наименование, артикул или штрихкод"
            type="search"
            value={draft.productSearch}
            onChange={actions.productSearchChanged}
          />
          {vm.products.status === 'loading' && <p className="onec-muted">Ищем товар…</p>}
          {vm.products.status === 'empty' && <p className="onec-muted">{vm.products.message}</p>}
          {vm.products.status === 'error' && <p className="onec-problem">{vm.products.message}</p>}
          {vm.products.status === 'ready' && (
            <ul className="onec-choice-list" aria-label="Найденные товары">
              {vm.products.items.map((item) => (
                <li key={item.key}>
                  <Button variant="secondary" onClick={() => actions.productChosen(item)}>
                    {item.label}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <div className="onec-row">
          <p>
            <strong>Товар:</strong> {draft.product.label}
          </p>
          <Button variant="ghost" onClick={actions.productCleared}>
            Другой товар
          </Button>
        </div>
      )}
      <div className="onec-row">
        <SelectField
          label="Вид цен"
          value={draft.priceType}
          options={priceTypes}
          disabled={priceTypes.length === 0}
          disabledReason={priceTypes.length === 0 ? 'Видов цен в срезе нет' : null}
          onChange={actions.priceTypeChanged}
        />
        <TextField label="Цена, ₽" value={draft.priceText} onChange={actions.priceChanged} placeholder="0,00" />
        <TextField label="Дата (ГГГГ-ММ-ДД, пусто — сегодня)" value={draft.period} onChange={actions.periodChanged} />
      </div>
      {vm.problem !== null && <p className="onec-muted">{vm.problem}</p>}
      {vm.error !== null && (
        <StatusMessage
          kind="error"
          title="Цена не записана"
          text={vm.error.message}
          meta={`Код: ${vm.error.code}${vm.error.correlationId === null ? '' : ' · запрос ' + vm.error.correlationId}`}
        />
      )}
      <div className="onec-row">
        <Button variant="primary" busy={vm.saving} disabled={vm.problem !== null} onClick={actions.save}>
          Записать цену
        </Button>
      </div>
    </section>
  )
}

/** Кнопка «Удалить» записи (строка списка: товар × вид цен) и её подтверждение: запись регистра в 1С удаляется насовсем, поэтому — вторым нажатием. */
export function PriceRemoveCell({
  rowKey,
  dateText,
  vm,
  actions,
}: {
  rowKey: string
  dateText: string
  vm: PriceManageVm['remove']
  actions: Pick<PriceManageActions, 'askRemove' | 'cancelRemove' | 'confirmRemove'>
}) {
  if (vm.confirmKey !== rowKey) {
    return (
      <Button variant="ghost" icon={<Trash2 size={16} aria-hidden="true" />} onClick={() => actions.askRemove(rowKey)}>
        Удалить
      </Button>
    )
  }
  return (
    <div className="onec-row">
      <span>Удалить запись цены от {dateText}?</span>
      <Button variant="primary" busy={vm.busy} onClick={actions.confirmRemove}>
        Да, удалить
      </Button>
      <Button variant="ghost" disabled={vm.busy} onClick={actions.cancelRemove}>
        Отмена
      </Button>
    </div>
  )
}
